import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MeetingFormatEnum } from "@/types/MeetingFormat";
import type { WebsiteDesignSettingsTheme } from "@/utils/websiteDesignTheme";
import { fetchPublicWebsiteDesignSettings } from "./fetchPublicWebsiteDesignSettings";
import { LivestreamGuestNameGate } from "./meeting/LivestreamGuestNameGate";
import { LivestreamMeetingExperience } from "./meeting/LivestreamMeetingExperience";
import { LivestreamWaitingScreen } from "./meeting/LivestreamWaitingScreen";
import { useLivestreamSchedulePhase } from "./meeting/useLivestreamSchedulePhase";
import { useMeetingSession } from "./meeting/useMeetingSession";
import { WebinarMeetingExperience } from "./meeting/WebinarMeetingExperience";
import { HavingTroubleLink } from "./meeting/HavingTroubleLink";
import { LivestreamStartDateDisplay } from "./meeting/LivestreamStartDateDisplay";
import { WebsiteThemeScope } from "./WebsiteThemeScope";

export function MeetingRoute() {
  const navigate = useNavigate();
  const [purchaseSubmitting, setPurchaseSubmitting] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [theme, setTheme] = useState<WebsiteDesignSettingsTheme | null>(null);

  useEffect(() => {
    void fetchPublicWebsiteDesignSettings().then(setTheme);
  }, []);
  const {
    productId,
    loading,
    notFound,
    error,
    needsAuth,
    needsGuestName,
    guestNameSubmitting,
    submitGuestName,
    productName,
    productImageUrl,
    pageTitle,
    guestNamePageTitle,
    authToken,
    meetingFormat,
    shopProductIds,
    shopTipping,
    leadContact,
    showLogout,
    loggingOut,
    handleLogout,
    ready,
    hasPrice,
    supportEmail,
    meetingStart,
    meetingEnd,
    isHostJoinLink,
    isAdminJoinLink,
    livestreamScheduleWaiting,
    joinWhenScheduleAllows,
  } = useMeetingSession();

  const schedulePhase = useLivestreamSchedulePhase({
    start: meetingStart,
    end: meetingEnd,
  });
  const joinAttemptedRef = useRef(false);

  useEffect(() => {
    joinAttemptedRef.current = false;
  }, [productId]);

  useEffect(() => {
    if (
      !livestreamScheduleWaiting ||
      meetingFormat !== MeetingFormatEnum.LIVESTREAM ||
      isHostJoinLink ||
      isAdminJoinLink ||
      joinAttemptedRef.current
    ) {
      return;
    }

    if (
      schedulePhase === "waiting-for-host" ||
      schedulePhase === "none" ||
      schedulePhase === "active"
    ) {
      joinAttemptedRef.current = true;
      void joinWhenScheduleAllows();
    }
  }, [
    isHostJoinLink,
    isAdminJoinLink,
    joinWhenScheduleAllows,
    livestreamScheduleWaiting,
    meetingFormat,
    schedulePhase,
  ]);

  async function startPurchaseAccess() {
    if (!productId || purchaseSubmitting) return;

    setPurchaseError(null);
    setPurchaseSubmitting(true);
    try {
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lineItems: [{ productId, quantity: 1 }],
          onCheckoutSuccess: [
            { type: "GoTo", route: `/meetings/${productId}/login` },
          ],
        }),
      });
      const data = (await response.json().catch(() => ({}))) as {
        error?: string;
        checkout?: { _id?: string };
      };
      if (!response.ok) {
        throw new Error(data.error ?? "Failed to start checkout");
      }
      const checkoutId = data.checkout?._id?.trim();
      if (!checkoutId) {
        throw new Error("Missing checkout");
      }
      navigate(`/checkout/${checkoutId}/contact`);
    } catch (e) {
      setPurchaseError(
        e instanceof Error ? e.message : "Failed to start checkout",
      );
    } finally {
      setPurchaseSubmitting(false);
    }
  }

  if (!productId) {
    return (
      <>
        <Helmet>
          <title>Meeting</title>
        </Helmet>
        <div className="min-h-dvh flex items-center justify-center bg-black text-white">
          Missing meeting ID
        </div>
      </>
    );
  }

  if (needsAuth) {
    if (hasPrice) {
      const displayName = productName.trim() || "Meeting";
      const accessPageTitle = `${displayName} · Get access`;
      return (
        <WebsiteThemeScope settings={theme}>
          <Helmet>
            <title>{accessPageTitle}</title>
          </Helmet>
          <div className="min-h-dvh text-white flex flex-col bg-black">
            <div className="flex flex-1 flex-col items-center justify-center px-6">
              <div className="w-full max-w-sm space-y-6 text-center">
                {productImageUrl ? (
                  <img
                    src={productImageUrl}
                    alt={displayName}
                    className="w-40 mx-auto rounded-lg shadow-xl object-cover"
                  />
                ) : null}
                <div>
                  <h1 className="text-2xl font-bold">{displayName}</h1>
                  <p className="text-gray-400 text-sm mt-2">
                    Purchase access to join this meeting
                  </p>
                  <LivestreamStartDateDisplay
                    start={meetingStart}
                    end={meetingEnd}
                    eventTitle={displayName}
                    meetingUrl={`/meetings/${productId}`}
                  />
                </div>
                {purchaseError ? (
                  <p className="text-sm text-red-400">{purchaseError}</p>
                ) : null}
                <Button
                  type="button"
                  className="w-full"
                  disabled={purchaseSubmitting}
                  onClick={() => void startPurchaseAccess()}
                >
                  {purchaseSubmitting ? "Loading…" : "Purchase access"}
                </Button>
                <p className="text-sm text-gray-400">
                  Already purchased?{" "}
                  <Link
                    to={`/meetings/${productId}/login`}
                    className="text-white underline underline-offset-2 hover:opacity-90"
                  >
                    Sign in
                  </Link>
                </p>
              </div>
            </div>
            <div className="shrink-0 px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
              <HavingTroubleLink supportEmail={supportEmail} />
            </div>
          </div>
        </WebsiteThemeScope>
      );
    }
    return <Navigate to={`/meetings/${productId}/login`} replace />;
  }

  if (needsGuestName) {
    return (
      <LivestreamGuestNameGate
        productName={productName.trim() || "Livestream"}
        imageUrl={productImageUrl}
        pageTitle={guestNamePageTitle}
        submitting={guestNameSubmitting}
        error={error}
        onSubmit={submitGuestName}
      />
    );
  }

  if (loading) {
    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
        </Helmet>
        <div className="min-h-dvh flex items-center justify-center bg-black text-white">
          Loading...
        </div>
      </>
    );
  }

  if (notFound) {
    return (
      <>
        <Helmet>
          <title>Meeting not found</title>
        </Helmet>
        <div className="min-h-dvh flex items-center justify-center bg-black text-white">
          Meeting not found
        </div>
      </>
    );
  }

  if (
    meetingFormat === MeetingFormatEnum.LIVESTREAM &&
    livestreamScheduleWaiting &&
    !isHostJoinLink &&
    !isAdminJoinLink
  ) {
    return (
      <LivestreamWaitingScreen
        productName={productName.trim() || "Livestream"}
        imageUrl={productImageUrl}
        pageTitle={pageTitle}
        start={meetingStart}
        end={meetingEnd}
        meetingUrl={`/meetings/${productId}`}
      />
    );
  }

  if (!ready || !authToken) {
    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
        </Helmet>
        <div className="min-h-dvh flex items-center justify-center bg-black text-white px-4 text-center">
          {error ?? "Unable to start meeting"}
        </div>
      </>
    );
  }

  const experienceProps = {
    authToken,
    meetingProductId: productId,
    shopProductIds,
    shopTipping,
    leadContact,
    showLogout,
    loggingOut,
    onLogout: handleLogout,
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      {meetingFormat === MeetingFormatEnum.LIVESTREAM ? (
        <LivestreamMeetingExperience
          {...experienceProps}
          meetingStart={meetingStart}
          meetingEnd={meetingEnd}
          isHostJoinLink={isHostJoinLink}
          isAdminJoinLink={isAdminJoinLink}
          productName={productName.trim() || "Livestream"}
          productImageUrl={productImageUrl}
        />
      ) : (
        <WebinarMeetingExperience {...experienceProps} />
      )}
    </>
  );
}
